import utils from '@/commonProject/helpers/utils';
import i18n from '@/common/i18n';
import { useLoginStore } from '@/stores/login/loginStore';
import { useLanguageStore } from '@/stores/common/languageStore';

export default {
    errorMessage,
    isLogoutToRedirect,
    logoutToRedirectUrl,
    isStatus200HasError,
};

/**
 * エラーコードと処理結果に対応したエラーメッセージを返却する。
 * 403:権限が不足している旨のエラーメッセージを返却
 * 404:processを参照し、各機能ごとのエラーメッセージを返却
 * 423:今は操作ができない状態である旨のエラーメッセージを返却
 * 503:メンテナンス中のため、使用できない旨のエラーメッセージを返却
 * 400,409,500:システムエラーが発生した旨のエラーメッセージを返却
 * @param {Object} error APIコールによるcatchしたエラー情報。
 * @param {String} process 処理内容(取得、作成、追加、更新、削除)
 * @return {String} message エラーメッセージ
 */
function errorMessage(error, process, message) {
    // APIコール時のステータスコードに応じたエラーメッセージを設定する。
    // デフォルトはシステムエラーのメッセージを設定する。
    let returnMessage = message.t('common.message.apiResponseSystemError');

    // APIコール時のcatchで「error」が取得できないなど致命的な状態を判定する。
    if (error !== undefined && error !== null && error !== '') {
        switch (error.status) {
            case 401:
                returnMessage = message.t('common.message.apiResponse401Error');
                break;
            case 403:
                returnMessage = message.t('common.message.apiResponse403Error');
                break;
            case 404:
                switch (process) {
                    case 'get':
                        returnMessage = message.t('common.message.apiResponse404GetError');
                        break;
                    case 'create':
                        returnMessage = message.t('common.message.apiResponse404CreateError');
                        break;
                    case 'add':
                        returnMessage = message.t('common.message.apiResponse404AddError');
                        break;
                    case 'update':
                        returnMessage = message.t('common.message.apiResponse404UpdateError');
                        break;
                    case 'delete':
                        returnMessage = message.t('common.message.apiResponse404DeleteError');
                        break;
                }
                break;
            case 423:
                returnMessage = message.t('common.message.apiResponse423ExclusionError');
                break;
            case 503:
                returnMessage = message.t('common.message.apiResponse503MaintenanceError');
                break;
            case 400:
            case 409:
            case 500:
            default:
                break;
        }
    }
    return returnMessage;
}

/**
 * 致命的なエラー発生などで、ログイン画面などへのリダイレクトを判定する。
 * @param {Object} error APIコールによるcatchしたエラー情報。
 * @return {Boolean} returnFlag エラー発生による画面リダイレクトの可否。
 */
function isLogoutToRedirect(error) {
    // 致命的なエラーの場合のリダイレクトの可否を判定する。
    // デフォルトはリダイレクトする設定。
    let returnFlag = true;

    // APIコール時のcatchで「error」が取得できないなど致命的な状態を判定する。
    if (error !== undefined && error !== null && error !== '') {
        // エラーのHTTPステータスコード別に画面遷移先を分岐する。
        switch (error.status) {
            case 400:
            case 403:
            case 404:
            case 423:
                // これらのエラーの場合は画面遷移させないのでfalseを設定。
                returnFlag = false;
                break;
            case 503:
            case 500:
            default:
                // 致命的なエラーのため画面遷移させるので、何もしない。
                break;
        }
    }
    return returnFlag;
}

/**
 * 「isLogoutToRedirect」メソッドと組み合わせて、致命的なエラー発生などで、
 * ログイン画面などへのリダイレクトする場合のURLを制御する。
 * @param {Object} error APIコールによるcatchしたエラー情報。
 * @return {String} redirectUrl リダイレクト先のURLを返却する。
 */
function logoutToRedirectUrl(error) {
    const loginStore = useLoginStore();
    // 致命的なエラーの場合のリダイレクト先のURLを設定する。
    // デフォルトはログイン画面へ遷移させる。
    let redirectUrl = '/login?vid=' + loginStore.getVendorId;

    // APIコール時のcatchで「error」が取得できないなど致命的な状態を判定する。
    if (error !== undefined && error !== null && error !== '') {
        // エラーのHTTPステータスコード別に画面遷移先を分岐する。
        switch (error.status) {
            case 503:
                // メンテナンス中画面に遷移する。
                redirectUrl = '/maintenance';
                break;
            case 500:
            default:
                // ログイン画面に遷移させる。
                break;
        }
    }
    return redirectUrl;
}

/**
 * APIの
 * @param {Object} response APIコールによるcatchしたエラー情報。
 * @return {Boolean}
 */
function isStatus200HasError(response) {
    const languageStore = useLanguageStore();

    // APIコール時のcatchで「error」が取得できないなど致命的な状態を判定する。
    if (!utils.isNullEmpty(response) && !utils.isNullEmpty(response.data.ApiRes) && !utils.isNullEmpty(response.data.ApiRes.Status) && response.data.ApiRes.Status === 0) {
        // APIの例外エラー存在しない場合。
        return { status: false, message: '' };
    } else {
        // APIの例外エラーが発生した場合。

        // APIからエラーメッセージが返却された場合。
        if (!utils.isNullEmpty(response.data.ApiRes.MessageJp) && languageStore.getLanguageValue === '1') {
            // 画面の表示モードが「日本語」の場合。
            return { status: true, message: response.data.ApiRes.MessageJp };
        } else if (!utils.isNullEmpty(response.data.ApiRes.MessageEn) && languageStore.getLanguageValue === '2') {
            // 画面の表示モードが「英語」の場合。
            return { status: true, message: response.data.ApiRes.MessageEn };
        }

        // APIで例外エラーが発生したが、専用のエラーメッセージが返却されなかった場合。
        // 画面で共通的なエラーメッセージを表示する。
        return { status: true, message: i18n.global.t('common.message.apiResponseUnmatchStatus') };
    }
}
